import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { isRealDate, without } from '@/components/catalog/form-utils';
import {
  Button,
  Checkbox,
  Combobox,
  ConfirmDialog,
  Field,
  Input,
  Modal,
  MultiCombobox,
  Skeleton,
  Textarea,
  Tooltip,
  type ComboboxOption,
} from '@/components/ui';
import { Money } from '@/components/ui/Money';
import { Notice } from '@/components/ui/Notice';
import { ApiError, api } from '@/lib/api';
import { proRataCreditLine } from '@/lib/credit-city-pro-rata';
import { CREDIT_NOTE_NOT_SENT_NOTE, editableOf } from '@/lib/credit-notes';
import { computeGeneratedLinePreview } from '@/lib/decimal';
import { formatDecimalString } from '@/lib/format';
import { useAuth } from '@/lib/use-auth';
import { currencyOptions, useCurrencyOptions } from '@/lib/use-invoice-options';
import { withStoredOption } from '@/lib/use-reference-options';
import { brandingThemeOptions, useXeroBrandingThemes } from '@/lib/use-xero-branding-themes';
import { useXero } from '@/lib/xero-context';
import type {
  CreditNote,
  CreditNoteCreateInput,
  CreditNoteUpdateInput,
  InvoiceProduct,
  ReferenceRecord,
} from '@/lib/types';

/* -------------------------------------------------------------------------
 * The header of a credit note — created here, and edited here.
 *
 * Modelled on `InvoiceFormModal`, which is the closest sibling, and it diverges
 * from it in the one place that matters: **two of the fields on a create are
 * unsettable afterwards.** Everything below is arranged around that.
 * --------------------------------------------------------------------- */

/**
 * What a caller may seed a NEW credit note with.
 *
 * ⚠️ **CREATE ONLY, and that is not a convention — it is the contract.**
 * `companyId` and `currency` are `.omit()`ed from `CreditNoteUpdateInput`, so a
 * prefill applied on an edit could only ever describe a write this form is not
 * allowed to make. The edit branch never reads this object.
 */
export interface CreditNoteFormPrefill {
  companyId?: number;
  /** The master's own `currencyCode`, verbatim. Never lower-cased anywhere. */
  currency?: string;
  subject?: string;
  creditReasonId?: number;
  /** The CUSTOMER's reference. ⚠️ Never seeded from a number of ours. */
  reference?: string;
}

/**
 * Which of a source invoice's lines the new note should be raised for.
 *
 * 🔴 **A NAMED UNION RATHER THAN `number[] | undefined`, AND THAT IS THE WHOLE
 * POINT OF IT.** The copy endpoint reads an ABSENT `invoiceProductIds` as *every
 * line on that invoice*, and `[]` as a **422** — two falsy values one keystroke
 * apart carrying opposite instructions, which is the trap `cityIds` and Xero's
 * `ContactPersons` already record. Handed a bare optional array, a caller that
 * forgot to populate it sends nothing and credits the whole invoice **with a
 * `201`**; there is no error anywhere and the customer is handed back more money
 * than anybody chose.
 *
 * So the two branches are NAMED and a caller must state which it means:
 *
 *  - `{ kind: 'ids' }` — exactly these lines, and they were on screen.
 *    `cities` names the lines credited for only SOME of their cities (owner
 *    2026-10-27), chosen on this same screen; every other listed line is
 *    credited whole. `[]` means no line is narrowed.
 *  - `{ kind: 'all' }` — every line, **unseen**. Reachable only where the lines
 *    could not be listed at all, which is the one case where "all of them,
 *    sight unseen" is honestly what somebody is being offered. The form says so
 *    in as many words before it offers it.
 */
export type CreditNoteLineSelection =
  | { kind: 'ids'; invoiceProductIds: number[]; cities: CreditNoteLineCities[] }
  | { kind: 'all' };

/**
 * One invoice line credited for a SUBSET of its cities.
 *
 * ⚠️ Sent only when fewer than ALL the line's cities (`effectiveCities ??
 * cities`) are chosen — including when "every city still available" is fewer
 * than all of them because an earlier credit took some. A whole-line copy
 * would otherwise claim the taken cities again and be refused (409).
 */
export interface CreditNoteLineCities {
  invoiceProductId: number;
  cityIds: number[];
}

export interface CreditNoteFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Present = edit. Absent = create. Nothing else distinguishes the two modes. */
  creditNote?: CreditNote;
  /**
   * The invoice this credit note is being raised FROM, if it is being raised
   * from one at all.
   *
   * ⚠️ **CREATE ONLY, like the two balance coordinates beside it** — and for a
   * blunter reason than theirs: a note that already exists has its own lines,
   * and a picker offering to copy more onto it is `CopyFromInvoiceDialog`'s job
   * on the record itself, not this form's. The edit branch never reads it.
   *
   * ⚠️ **Its presence is what puts the line picker on screen.** A credit note
   * raised from nothing — the complimentary or goodwill case — has no source to
   * pick over, and the section is absent rather than empty.
   */
  sourceInvoice?: { id: number; currency: string | null };
  /**
   * The account this form was opened from, with its NAME.
   *
   * The name is not decoration: the locked row renders it as read-only text
   * rather than a picker, so an id with no name would render a blank box over a
   * company somebody is about to credit.
   */
  lockedCompany?: { id: number; name: string };
  prefill?: CreditNoteFormPrefill;
  /**
   * The note was written.
   *
   * ⚠️ **`lines` is present only on a CREATE FROM AN INVOICE**, and copying them
   * is deliberately NOT this form's job. Phase two is a second request with no
   * rollback behind it, so whoever owns the flow owns the report: the note may
   * exist while its lines do not, and the only honest sentence for that names
   * both halves. A form that copied on its own behalf would have to either
   * swallow that failure or invent a rollback it cannot perform.
   */
  onSaved: (
    note: CreditNote,
    lines?: CreditNoteLineSelection,
    followUp?: CreditNoteFollowUp,
  ) => void;
}

/**
 * What a CREATE asked to happen once the note (and its lines) exist (owner
 * 2026-10-28): submit it for approval, or approve it outright.
 *
 * ⚠️ **Handed to the caller, never run here**, for the reason on `lines`
 * above: approving auto-allocates the note to its source invoice, so it must
 * run AFTER phase two has copied the lines, and only the flow owner knows when
 * that is. `undefined` = a plain create; the note stays Draft.
 *
 * ⚠️ Offered by grant — `update` for submit, `approve` for approve (the grant
 * the Accounts set lacks, separation of duties). Neither emails the customer.
 */
export type CreditNoteFollowUp = 'submit' | 'approve';

/** The list ceiling on both lookups; the default of 20 is not the whole list. */
const OPTION_PER_PAGE = 100;

/* -------------------------------------------------------------------------
 * 🔴 THE TWO BALANCE COORDINATES, AND WHY THEY LIVE IN A SEPARATE OBJECT
 * --------------------------------------------------------------------- */

/**
 * Every field this form may write on an EDIT.
 *
 * 🔴 **`companyId` AND `currency` ARE ABSENT, AND THE ABSENCE IS THE GUARD.**
 * They are not omitted from a payload by an `if` somebody can drop — there is no
 * key here to name, so:
 *
 *  - no control can bind to one on the edit branch;
 *  - `buildUpdatePayload` takes this type, so `values.currency` does not compile;
 *  - `EDIT_WRITE_KEYS` is `Object.keys` of this object, so a 422 naming either
 *    cannot be claimed as markable and goes to the banner instead.
 *
 * That is the three-places rule held structurally rather than remembered, the
 * same shape as `projectId` on the three document forms and `ownerId` on a
 * quote. `CreditNoteUpdateInput` refuses them as well — two independent
 * defences, because this one is the one a later refactor could dissolve.
 *
 * **WHY, and it is money rather than tidiness.** `(companyId, currency)` is the
 * identity a credit BALANCE is struck at. Moving either after an allocation
 * exists moves money between accounts, or between denominations, **with a
 * `200`**: the allocations go on pointing at invoices that are now on a
 * different company or in a different currency, every figure still renders, and
 * nothing anywhere reports it. A credit note is re-raised, never re-homed.
 */
interface SharedValues {
  subject: string;
  contactId: string;
  creditReasonId: string;
  creditReasonDetail: string;
  creditNoteDate: string;
  reference: string;
  xeroBrandingThemeId: string;
}

/**
 * The two keys a CREATE may state, held apart from `SharedValues` so that a
 * function which takes the shared object cannot reach them.
 *
 * ⚠️ `null` on an edit, rather than an object of empty strings — an empty string
 * is a value, and a shape that merely *happens* to be blank is one narrowing
 * away from being sent.
 */
interface BalanceCoordinates {
  companyId: string;
  currency: string;
}

/*
 * A compile-time restatement of the paragraph above: if either coordinate is
 * ever added to `SharedValues`, this stops type-checking. Prose goes stale
 * silently; this does not.
 *
 * ⚠️ **Both sides are bracketed deliberately.** A bare `T extends never` is
 * DISTRIBUTIVE over a union and answers `never` — not `true` — for an empty
 * one, so the guard would resolve to `never` while the two types were still
 * correctly separate and fail the build for the opposite reason to the one it
 * exists for. The tuple form is not distributive and asks the question meant.
 */
type _CoordinatesAreNotShared = [Extract<keyof SharedValues, keyof BalanceCoordinates>] extends [
  never,
]
  ? true
  : never;
const _coordinatesAreNotShared: _CoordinatesAreNotShared = true;
void _coordinatesAreNotShared;

/**
 * What a credit note holds today, as form strings.
 *
 * ⚠️ **No `statusId` baseline, on either branch.** A status MOVES through
 * `/approve` and `/void`, which carry a fifth permission the Accounts access
 * level does not hold — a writable status here would hand approval to everybody
 * holding `update`, with no refusal to notice and no audit trail saying so. It
 * is absent from both write inputs, so there is nothing to seed.
 *
 * ⚠️ **No `xeroCreditNoteNumber` and no `xeroCreditNoteId` either.** Both are
 * written by the push and by nothing else; a client value is a second writer
 * that loses silently on the next push — and the number column is UNIQUE, so a
 * typed value can collide with a real Xero number and answer a 409 on a field
 * nobody can reconcile.
 *
 * ⚠️ **And none of the four totals.** They have two competing writers — a line
 * write rewrites all four inside its own transaction, a header PATCH writes
 * exactly what it is given and never re-derives — so a totals key in the same
 * body as a letterhead change is how a real figure is rewritten by an edit
 * nobody thought was about money. Same ruling as the invoice header totals.
 */
function toSharedValues(note?: CreditNote): SharedValues {
  return {
    subject: note?.subject ?? '',
    contactId: note?.contact ? String(note.contact.id) : '',
    // `creditReason` is NOT NULL on the wire, so on an edit this is never ''.
    creditReasonId: note?.creditReason ? String(note.creditReason.id) : '',
    creditReasonDetail: note?.creditReasonDetail ?? '',
    creditNoteDate: note?.creditNoteDate ?? '',
    reference: note?.reference ?? '',
    xeroBrandingThemeId: note?.xeroBrandingThemeId ?? '',
  };
}

/**
 * The `path` values a 422 may name that the EDIT branch can actually mark.
 *
 * Derived from the value object rather than written out, so it cannot drift from
 * the controls — and so `companyId` and `currency` fall out of it for free.
 * A path outside this list goes to the form-level banner, because a path can be
 * non-empty and still bind to nothing: routed to an input that does not exist it
 * vanishes from the banner *and* inflates the "fields need attention" count,
 * which is the "one field needs attention, highlighted below" lie with nothing
 * highlighted.
 */
const EDIT_WRITE_KEYS: readonly string[] = Object.keys(toSharedValues());

/** The same list, plus the two keys only a create may send. */
const CREATE_WRITE_KEYS: readonly string[] = [...EDIT_WRITE_KEYS, 'companyId', 'currency'];

/* -------------------------------------------------------------------------
 * Payloads
 * --------------------------------------------------------------------- */

/**
 * The CREATE body.
 *
 * ⚠️ **Built key by key, never from a spread.** `tsc` catches an excess property
 * on an object literal and **not** inside a conditional spread, which is exempt
 * from excess-property checking and serialises happily at runtime — this repo
 * has already shipped an undeclared request key exactly that way, green build
 * and all.
 *
 * ⚠️ **`currency` goes out VERBATIM — no `.toUpperCase()`, ever.** The value can
 * only have come from the active `/currencies` master, so folding it would buy
 * nothing; and `f_currencies` is a case-insensitive collation, so a folded
 * `"usd"` would *match* the USD row on lookup while never having been validated.
 * A lookup that succeeds is not a validation that passed.
 */
function buildCreatePayload(
  values: SharedValues,
  coordinates: BalanceCoordinates,
): CreditNoteCreateInput {
  const payload: CreditNoteCreateInput = {
    subject: values.subject.trim(),
    companyId: Number(coordinates.companyId),
    creditReasonId: Number(values.creditReasonId),
    currency: coordinates.currency.trim(),
  };

  /*
   * Unset keys are OMITTED, never sent as an explicit `null`. On a create the
   * two read the same to the server, and omission is what this app sends for
   * every unset field everywhere else.
   */
  if (values.contactId.trim() !== '') payload.contactId = Number(values.contactId.trim());
  if (values.creditReasonDetail.trim() !== '') {
    payload.creditReasonDetail = values.creditReasonDetail.trim();
  }
  if (values.creditNoteDate.trim() !== '') payload.creditNoteDate = values.creditNoteDate.trim();
  if (values.reference.trim() !== '') payload.reference = values.reference.trim();
  if (values.xeroBrandingThemeId.trim() !== '') {
    payload.xeroBrandingThemeId = values.xeroBrandingThemeId.trim();
  }

  return payload;
}

/**
 * The PATCH body for a given set of changed keys.
 *
 * ⚠️ **Only what CHANGED.** Across this API an omitted key is untouched and an
 * explicit `null` CLEARS, so spreading a loaded record into a PATCH wipes every
 * column the user never opened. An empty body is a 422, which is why Save is
 * disabled when nothing has moved.
 *
 * ⚠️ **It takes `SharedValues` and nothing else**, which is what makes the two
 * balance coordinates unreachable from here rather than merely unsent.
 */
function buildUpdatePayload(
  keys: readonly (keyof SharedValues)[],
  values: SharedValues,
): CreditNoteUpdateInput {
  const payload: CreditNoteUpdateInput = {};

  for (const key of keys) {
    const next = values[key].trim();

    if (key === 'subject') {
      // Not nullable server-side: `subject: null` is a 422. `validate` refuses
      // an empty one before this is ever reached.
      payload.subject = next;
    } else if (key === 'creditReasonId') {
      /*
       * ⚠️ **NOT NULL, so there is no clear.** The picker offers no empty row
       * for the same reason — a credit note with no stated reason is not
       * expressible, which is the point: this is the field an auditor reads.
       */
      payload.creditReasonId = Number(next);
    } else if (key === 'contactId') {
      // Genuinely nullable: `null` CLEARS, which is what an emptied picker means.
      payload.contactId = next === '' ? null : Number(next);
    } else {
      // The four nullable text columns. `''` is not a value on any of them, so
      // an emptied box is an explicit `null` and clears the column.
      payload[key] = next === '' ? null : next;
    }
  }

  return payload;
}

/* -------------------------------------------------------------------------
 * The three lookups
 * --------------------------------------------------------------------- */

/**
 * Companies for the picker, searched server-side.
 *
 * ⚠️ A near-copy of the hooks in `accounts-dashboard/FutureDocumentDialog` and
 * `invoice-tracker/StandaloneRowDialog`, and deliberately not an import from
 * either: exporting a non-component from a component file is the one thing in
 * `src/` that breaks fast refresh. Thirty lines of duplication against every
 * screen in this repo losing its refresh behaviour.
 */
function useCompanyOptions(enabled: boolean) {
  const [term, setTerm] = useState('');
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [total, setTotal] = useState(0);
  const [failed, setFailed] = useState(false);
  /*
   * The term the loaded options answer — null until the first read settles.
   * Deriving `loading` from it rather than holding a flag means a new term reads
   * as loading during the very render that set it, with no setState inside the
   * effect to start a second render pass.
   */
  const [loadedTerm, setLoadedTerm] = useState<string | null>(null);
  const loading = enabled && loadedTerm !== term;

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const { signal } = controller;

    api
      .listCompanies(
        {
          page: 1,
          perPage: OPTION_PER_PAGE,
          sort: 'name',
          order: 'asc',
          ...(term ? { search: term } : {}),
        },
        signal,
      )
      .then((result) => {
        setOptions(result.data.map((company) => ({ value: String(company.id), label: company.name })));
        setTotal(result.meta.total);
        setFailed(false);
      })
      .catch(() => {
        /*
         * ⚠️ `failed`, not merely empty. Both render no options, and telling
         * somebody there are no companies when the request simply did not come
         * back sends them hunting for an account that is on file.
         */
        if (signal.aborted) return;
        setOptions([]);
        setFailed(true);
      })
      .finally(() => {
        if (!signal.aborted) setLoadedTerm(term);
      });

    return () => controller.abort();
  }, [term, enabled]);

  return { options, total, loading, failed, setTerm };
}

/**
 * Contacts for the picker, scoped to the company the note names.
 *
 * ⚠️ **Scoped, and cleared with the company.** `contacts.service.ts` checks a
 * contact for EXISTENCE and never against the company, so a person left behind
 * by a company change is accepted with a `200` — a stored, plausible, wrong
 * person on a document that moves money. The clearing in `onCompanyChange` is
 * the only guard there is.
 */
function useContactOptions(companyId: number | null) {
  const [term, setTerm] = useState('');
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [failed, setFailed] = useState(false);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const key = `${companyId ?? ''}:${term}`;
  const loading = loadedKey !== key;

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    api
      .listContacts(
        {
          page: 1,
          perPage: OPTION_PER_PAGE,
          ...(companyId === null ? {} : { companyId }),
          ...(term ? { search: term } : {}),
        },
        signal,
      )
      .then((result) => {
        setOptions(result.data.map((contact) => ({ value: String(contact.id), label: contact.name })));
        setFailed(false);
      })
      .catch(() => {
        if (signal.aborted) return;
        setOptions([]);
        setFailed(true);
      })
      .finally(() => {
        if (!signal.aborted) setLoadedKey(key);
      });

    return () => controller.abort();
  }, [companyId, term, key]);

  return { options, loading, failed, setTerm };
}

/**
 * The credit-reason master, active rows only.
 *
 * 🔴 **`api.listCreditNoteReasons` IS THE ONE THING THIS FILE DEPENDS ON AND
 * DOES NOT OWN.** `/credit-note-reasons` is a reference-registry master — the
 * `ReferenceRecord` row shape, the `isActive` filter, the `perPage` ceiling of
 * 200, `sortOrder ASC, name ASC` order — so the method is one line beside
 * `listInvoiceStatuses` in `api.ts`:
 *
 * ```ts
 * listCreditNoteReasons: (params: ReferenceListParams = {}, signal?: AbortSignal) =>
 *   listReference('credit-note-reasons', params, signal),
 * ```
 *
 * It is called rather than worked around deliberately. Every API call in this
 * app goes through `api.ts`; a `fetch` here, or a prop asking the page to do the
 * fetching, would be a second transport for one resource and would put the
 * `isActive` decision somewhere it can be forgotten. **A missing method is a
 * build error somebody fixes in one line; a second fetch path is a defect
 * nothing reports.**
 *
 * ⚠️ **`isActive: true` is opt-in with no default.** Unfiltered returns retired
 * rows too and a retired id is a 422 even when nothing about it changed — so
 * offering one is offering a guaranteed refusal.
 */
function useCreditReasonOptions() {
  const [records, setRecords] = useState<ReferenceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    api
      .listCreditNoteReasons({ page: 1, perPage: 200, isActive: true }, signal)
      .then((result) => {
        if (signal.aborted) return;
        setRecords(result.data);
        setFailed(false);
      })
      .catch(() => {
        if (signal.aborted) return;
        // Empty on failure — but SAY so, so the field can render a refusal
        // instead of an innocent-looking empty list.
        setRecords([]);
        setFailed(true);
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, []);

  return { records, loading, failed };
}

/**
 * The lines of the invoice this note is being raised from.
 *
 * ⚠️ **A FAILED READ IS NOT AN EMPTY INVOICE, and the two must never collapse
 * into one state here.** Both render no rows; one means *this invoice has
 * nothing on it* and the other means *we could not ask*. They lead to opposite
 * offers — an invoice with no lines gives a person nothing to credit, while an
 * unread one can still be credited whole — so `lines` stays `null` on failure
 * and `failed` says which of the two it is.
 *
 * ⛔ **No `?? []` anywhere between this and the screen.** That one character
 * turns *we could not ask* into *this invoice sells nothing*, which is the
 * quieter and more confident of the two wrong answers.
 */
function useSourceInvoiceLines(invoiceId: number | null) {
  const [lines, setLines] = useState<InvoiceProduct[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  /*
   * The invoice the loaded state answers for — `null` until the first read
   * settles. **Deriving `loading` from it rather than holding a flag** is the
   * shape `useContactOptions` above uses, and it is not a style choice: a
   * `setLoading(true)` at the top of the effect is a synchronous setState
   * inside an effect, which starts a second render pass for a value that was
   * already derivable. This way a new invoice reads as loading during the very
   * render that asked for it.
   */
  const [loadedId, setLoadedId] = useState<number | null>(null);
  const loading = invoiceId !== null && loadedId !== invoiceId;

  useEffect(() => {
    if (invoiceId === null) return;
    const controller = new AbortController();
    const { signal } = controller;

    api
      .listInvoiceProducts(invoiceId, signal)
      .then((result) => {
        if (signal.aborted) return;
        setLines(result.data);
        setFailed(null);
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setLines(null);
        setFailed(
          caught instanceof ApiError
            ? (caught.formMessage ?? caught.message)
            : 'The invoice’s lines could not be listed.',
        );
      })
      .finally(() => {
        if (!signal.aborted) setLoadedId(invoiceId);
      });

    return () => controller.abort();
  }, [invoiceId]);

  return { lines, loading, failed };
}

/* -------------------------------------------------------------------------
 * Crediting some of a line's cities (owner 2026-10-27)
 * --------------------------------------------------------------------- */

type LineCityRef = { id: number; name: string };

/** Why a line whose every city is already on a credit note cannot be ticked. */
const ALL_CITIES_CREDITED_REASON = 'Every city on this line has already been credited.';

/**
 * The cities an invoice line really runs in, and N for pro-rata.
 *
 * ⚠️ `effectiveCities` first: a line flagged "all of the project's cities"
 * stores NO city rows, so `cities` alone would read as a line with none.
 * `undefined` is an older API, which only ever had the stored rows.
 */
function sourceCitiesOf(line: InvoiceProduct): LineCityRef[] {
  return line.effectiveCities ?? line.cities;
}

/** The line's cities not yet on a non-void credit line. */
function availableCitiesOf(line: InvoiceProduct): LineCityRef[] {
  // `undefined` = an older API that does not report it; nothing is known to be taken.
  const taken = new Set(line.creditedCityIds ?? []);
  return sourceCitiesOf(line).filter((city) => !taken.has(city.id));
}

/** A line with cities, every one of them already credited. */
function isFullyCredited(line: InvoiceProduct): boolean {
  return sourceCitiesOf(line).length > 0 && availableCitiesOf(line).length === 0;
}

/**
 * The credit line's total for `chosen` of the line's cities, or `null` where no
 * figure can be stated (no cities chosen, an unpriced line, a malformed value).
 *
 * ⚠️ A PREVIEW of the server's pricing, never a write: the server prices a
 * city subset with the same `proRataCreditLine`, and the figure stays editable
 * on the credit note afterwards.
 */
function creditLineTotal(line: InvoiceProduct, chosen: number): string | null {
  const lineCities = sourceCitiesOf(line).length;
  if (chosen < 1 || chosen > lineCities) return null;
  if (chosen === lineCities) return line.lineTotal;
  if (line.unitPrice === null) return null;
  let priced: { unitPrice: string; discountAmount: string | null };
  try {
    priced = proRataCreditLine({
      unitPrice: line.unitPrice,
      discountAmount: line.discountAmount,
      lineCities,
      creditedCities: chosen,
    });
  } catch {
    return null;
  }
  return (
    computeGeneratedLinePreview({
      quantity: String(line.quantity),
      unitPrice: priced.unitPrice,
      discount: line.discount,
      discountAmount: priced.discountAmount,
      taxRate: line.taxRate,
    })?.lineTotal ?? null
  );
}

/* -------------------------------------------------------------------------
 * The modal
 * --------------------------------------------------------------------- */

/**
 * Create or edit a credit note's header.
 *
 * ⚠️ **This form does NOT gate its own Save on a permission**, exactly as
 * `InvoiceFormModal` does not: **every caller gates the affordance**. A modal
 * offering a Save that would 403 is worse than no control at all, and the
 * caller is the thing that knows whether it is offering a create or an edit.
 *
 * ⚠️ **Unmounted when closed**, so every open starts from a fresh baseline —
 * a stale baseline is a diff against a record that has since moved.
 */
export function CreditNoteFormModal(props: CreditNoteFormModalProps) {
  if (!props.open) return null;
  return <CreditNoteForm {...props} />;
}

function CreditNoteForm({
  onClose,
  creditNote,
  lockedCompany,
  prefill,
  sourceInvoice,
  onSaved,
}: CreditNoteFormModalProps) {
  const isEdit = creditNote !== undefined;
  const formId = useId();
  const subjectRef = useRef<HTMLInputElement | null>(null);

  /** The edit baseline, captured once. Never re-seeded — see the unmount note. */
  const [baseline] = useState<SharedValues>(() => toSharedValues(creditNote));
  const [values, setValues] = useState<SharedValues>(() => {
    const base = toSharedValues(creditNote);
    if (isEdit || !prefill) return base;
    return {
      ...base,
      ...(prefill.subject === undefined ? {} : { subject: prefill.subject }),
      ...(prefill.reference === undefined ? {} : { reference: prefill.reference }),
      ...(prefill.creditReasonId === undefined
        ? {}
        : { creditReasonId: String(prefill.creditReasonId) }),
    };
  });

  /**
   * The two balance coordinates — **`null` on an edit, and the type is the
   * guard.** Nothing downstream can read a coordinate without narrowing this
   * first, and the edit branch never narrows it.
   */
  const [coordinates, setCoordinates] = useState<BalanceCoordinates | null>(() => {
    if (isEdit) return null;
    return {
      companyId: lockedCompany
        ? String(lockedCompany.id)
        : prefill?.companyId === undefined
          ? ''
          : String(prefill.companyId),
      currency: prefill?.currency ?? '',
    };
  });

  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submitLock = useRef(false);

  /*
   * The create-and-then buttons (owner 2026-10-28). Create only; each is
   * offered by the grant the server checks for its follow-up.
   */
  const { ability } = useAuth();
  const canSubmitFollowUp = !isEdit && ability.canUpdate('credit-notes');
  const canApproveFollowUp = !isEdit && ability.canApprove('credit-notes');
  /** Which button's save is in flight, so only that one spins. */
  const [savingAs, setSavingAs] = useState<CreditNoteFollowUp | 'plain' | null>(null);
  /** The follow-up awaiting its confirmation. */
  const [confirmFollowUp, setConfirmFollowUp] = useState<CreditNoteFollowUp | null>(null);

  const errors = useMemo(() => ({ ...clientErrors, ...serverErrors }), [clientErrors, serverErrors]);

  /*
   * ⚠️ **The edit gate, and it belongs to `@/lib/credit-notes` rather than
   * here.** `editableOf` reads the STATUS's `isEditable` FLAG and never a status
   * NAME — an admin-editable master's name is a label somebody can rename with
   * no migration and no notification, and this codebase has been bitten by
   * exactly that four times.
   *
   * ⚠️ **Do NOT add a Xero test of your own beside it.** That module records the
   * second half of the rule as a known gap closed in one place; a per-surface
   * guard is how two screens come to disagree about whether one note may be
   * edited.
   */
  const editVerdict = creditNote ? editableOf(creditNote) : null;
  const locked = editVerdict !== null && !editVerdict.editable;
  const lockedReason = editVerdict !== null && !editVerdict.editable ? editVerdict.reason : null;

  /* ── the three lookups ───────────────────────────────────────────────── */

  const companyPickerUsed = coordinates !== null && lockedCompany === undefined;
  const companies = useCompanyOptions(companyPickerUsed);
  const companiesFootnote =
    companies.total > companies.options.length
      ? `Showing ${companies.options.length} of ${companies.total}. Type to search all.`
      : undefined;

  /*
   * The company the CONTACT list is scoped to — the coordinate on a create, the
   * record's own (unchangeable) company on an edit.
   */
  const contactScopeCompanyId = coordinates
    ? coordinates.companyId === ''
      ? null
      : Number(coordinates.companyId)
    : (creditNote?.company.id ?? null);
  const contacts = useContactOptions(contactScopeCompanyId);

  const reasons = useCreditReasonOptions();
  /*
   * ⛔ **No `emptyOptionLabel` on this picker, deliberately.** The column is
   * `NOT NULL`: there is no "no reason" state to offer, and a row that selects
   * one would be an option whose only outcome is a 422.
   *
   * A stored retired reason is injected back, labelled — `withStoredOption`
   * solves the deactivation trap once for the whole app, and it withholds the
   * "retired" label while the list is empty, because absence proves nothing
   * until there is a list to be absent from.
   */
  const reasonOptions = useMemo(
    () => withStoredOption(reasons.records, creditNote?.creditReason ?? null),
    [reasons.records, creditNote],
  );

  /*
   * ⚠️ **`isActive: true` lives inside `useCurrencyOptions`** — only GBP, EUR and
   * USD are active, 3 of 164 rows. A picker that omitted it would offer 161
   * codes no new document should carry.
   *
   * ⚠️ **And `currencyOptions` pins a STORED code back into the list.** Reads
   * never filter on `isActive`, so render what you were given and narrow only
   * what you OFFER. It only fires on a create here, where the value comes from
   * a prefill; the edit branch has no currency control at all.
   */
  const currencies = useCurrencyOptions(coordinates !== null);
  const currencyPickerOptions = useMemo(
    () => currencyOptions(currencies.currencies, coordinates?.currency || null),
    [currencies.currencies, coordinates],
  );

  /*
   * ⚠️ **A rate-limited Xero read, gated on the connection.** Disconnected, the
   * request could only fail, and a red block is the wrong sentence for a form
   * whose real state is "Xero is not connected".
   *
   * ⚠️ **Known cost, stated rather than discovered:** this fires on a CREATE as
   * well as an edit, so a new credit note spends one Xero call against a budget
   * shared by the whole office. `QuoteFormModal` gates its letterhead picker on
   * `isEdit` for exactly that reason and `InvoiceFormModal` does not — a live
   * divergence between the two document forms rather than a settled rule. This
   * follows the invoice, because the letterhead is offered on both branches
   * here; withholding the control on a create would be the other decision.
   */
  const xero = useXero();
  const xeroConnected = !xero.loading && xero.connection.connected;
  const brandingThemes = useXeroBrandingThemes(xeroConnected);
  const themeOptions = useMemo(
    () =>
      brandingThemeOptions(
        brandingThemes.themes,
        values.xeroBrandingThemeId || null,
        // A credit note carries no cached theme NAME, so the injected row falls
        // back to the id — which reads badly and is still better than a blank
        // row, because a blank row reads as a choice.
        null,
      ),
    [brandingThemes.themes, values.xeroBrandingThemeId],
  );

  /* ── the lines being credited ────────────────────────────────────────── */

  /*
   * ⚠️ **`null` on an EDIT even when a caller passes a source**, rather than
   * trusting every call site to withhold the prop on the edit branch. The
   * picker writes nothing this form sends, so a stray prop would spend a
   * request and put a control on screen whose only outcome is nothing.
   */
  const sourceInvoiceId = isEdit ? null : (sourceInvoice?.id ?? null);
  const sourceLines = useSourceInvoiceLines(sourceInvoiceId);

  /**
   * 🔴 **THE IDS THE READER HAS UNTICKED — the complement of what is selected,
   * and holding it this way round is what makes "all ticked" need no seeding
   * step at all.**
   *
   * The obvious shape is a set of TICKED ids seeded from the list once it
   * arrives, and it costs two things this does not: a `null` "not seeded yet"
   * state that every reader downstream has to tell apart from "the reader
   * cleared everything" — two opposite instructions wearing one empty-looking
   * value — and a `setState` inside an effect to do the seeding, which starts a
   * second render pass for something derivable.
   *
   * ⚠️ **Empty means EVERY line is credited**, which is the default the owner
   * chose and the branch the copy endpoint itself treats as the no-key case.
   * Pressing straight through gives exactly the behaviour this flow had before
   * the picker existed.
   *
   * ⚠️ **An id here for a line not in the list is INERT, never wrong.** Nothing
   * enumerates this set — it is only ever asked whether it contains a given
   * line's id — so a stale entry cannot resurrect or hide a row.
   */
  const [untickedLineIds, setUntickedLineIds] = useState<Set<number>>(new Set());

  /**
   * The cities chosen per line, keyed by invoice line id. ⚠️ **ABSENT means
   * every city still available** — the same complement trick as the unticked
   * set above, so a fresh list needs no seeding step and "credit the rest of
   * the line" is what pressing straight through does.
   */
  const [cityChoices, setCityChoices] = useState<Map<number, string[]>>(new Map());

  function chosenCityIdsOf(line: InvoiceProduct): string[] {
    return cityChoices.get(line.id) ?? availableCitiesOf(line).map((city) => String(city.id));
  }

  const sourceLineList = sourceLines.lines;
  /** A choice is owed only where there are real lines to choose between. */
  const lineChoiceOwed = sourceLineList !== null && sourceLineList.length > 0;
  /** A line whose every city is already credited cannot be ticked at all. */
  const selectableLines = (sourceLineList ?? []).filter((line) => !isFullyCredited(line));
  const selectedLines = selectableLines.filter((line) => !untickedLineIds.has(line.id));
  const selectedLineCount = selectedLines.length;
  const lineSelectionEmpty = lineChoiceOwed && selectedLineCount === 0;
  const everyLineCredited = lineChoiceOwed && selectableLines.length === 0;
  /** A ticked line with cities must keep at least one; an empty set credits nothing. */
  const missingCities = selectedLines.some(
    (line) => sourceCitiesOf(line).length > 0 && chosenCityIdsOf(line).length === 0,
  );

  function toggleLine(id: number, checked: boolean) {
    setUntickedLineIds((prev) => {
      const next = new Set(prev);
      // Ticked = absent from the exclusion set. The inversion is the whole
      // mechanism, so it is spelled out rather than left to read as a bug.
      if (checked) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /**
   * What the caller should copy, or `undefined` where nothing should be.
   *
   * ⛔ **`{ kind: 'all' }` IS GATED ON THE READ HAVING FAILED, not on the list
   * being absent.** `lines === null` is also true while the request is in
   * flight, so keying on it would promise "every line on this invoice" for the
   * second before the list arrives — and somebody who presses in that second
   * gets exactly what it said, which is not what they were about to be shown.
   * Save is disabled through that second for the same reason.
   */
  function lineSelection(): CreditNoteLineSelection | undefined {
    if (sourceInvoiceId === null) return undefined;
    if (sourceLines.failed !== null) return { kind: 'all' };
    if (sourceLineList === null) return undefined;
    // An invoice with no lines has nothing to copy; a request naming none
    // would be a 422, and an absent key would credit an empty invoice whole.
    if (sourceLineList.length === 0) return undefined;
    /*
     * 🔴 **`cities` NAMES A LINE WHENEVER FEWER THAN ALL ITS CITIES ARE
     * CHOSEN** — fewer than the line's `effectiveCities ?? cities`, not fewer
     * than what is still available. Once an earlier credit took some cities,
     * "every city left" is already a subset and must be sent as one, or the
     * whole-line copy claims the taken cities again and is refused (409).
     */
    const cities: CreditNoteLineCities[] = [];
    for (const line of selectedLines) {
      const lineCities = sourceCitiesOf(line).length;
      if (lineCities === 0) continue;
      const cityIds = chosenCityIdsOf(line).map(Number);
      if (cityIds.length < lineCities) cities.push({ invoiceProductId: line.id, cityIds });
    }
    return { kind: 'ids', invoiceProductIds: selectedLines.map((line) => line.id), cities };
  }

  /* ── the diff ────────────────────────────────────────────────────────── */

  const changedKeys = useMemo(() => {
    const keys = Object.keys(baseline) as (keyof SharedValues)[];
    return keys.filter((key) => values[key].trim() !== baseline[key].trim());
  }, [values, baseline]);
  const isDirty = changedKeys.length > 0;

  function setField(key: keyof SharedValues, next: string) {
    setValues((prev) => ({ ...prev, [key]: next }));
    setClientErrors((prev) => (key in prev ? without(prev, key) : prev));
    setServerErrors((prev) => (key in prev ? without(prev, key) : prev));
  }

  function setCoordinate(key: keyof BalanceCoordinates, next: string) {
    setCoordinates((prev) => (prev === null ? prev : { ...prev, [key]: next }));
    setClientErrors((prev) => (key in prev ? without(prev, key) : prev));
    setServerErrors((prev) => (key in prev ? without(prev, key) : prev));
  }

  /**
   * Changing the company CLEARS the contact.
   *
   * ⚠️ **The server will not catch this one** — a contact is checked for
   * existence and never against the company — so a contact left behind by a
   * company change is stored with a `200`. One state update, because the two
   * have to move together.
   *
   * ⚠️ Only on an actual CHANGE: re-picking the same company must not discard a
   * contact somebody deliberately chose.
   */
  function onCompanyChange(next: string) {
    if (coordinates === null || next === coordinates.companyId) return;
    setCoordinates((prev) => (prev === null ? prev : { ...prev, companyId: next }));
    setValues((prev) => ({ ...prev, contactId: '' }));
    setClientErrors((prev) => without(without(prev, 'companyId'), 'contactId'));
    setServerErrors((prev) => without(without(prev, 'companyId'), 'contactId'));
  }

  /* ── validation ──────────────────────────────────────────────────────── */

  /*
   * ⚠️ **No invented length caps.** `subject`, `reference` and
   * `creditReasonDetail` have column limits this client has not measured, and a
   * `maxLength` guessed too low TRUNCATES a customer-facing string in the
   * browser with nothing to report — where a guess too high is an ordinary 422
   * the routing below marks on the field. Over-offering is the safe direction;
   * add a cap when the schema has been read, not before.
   */
  function validate(): Record<string, string> {
    const found: Record<string, string> = {};

    if (values.subject.trim() === '') found.subject = 'Subject is required.';

    if (values.creditReasonId.trim() === '') {
      // Mirrors the NOT NULL column, which is why the picker offers no empty row.
      found.creditReasonId = 'Choose why this credit is being raised.';
    }

    if (coordinates !== null) {
      if (coordinates.companyId.trim() === '') {
        found.companyId = 'Choose the company being credited.';
      }
      const currency = coordinates.currency.trim();
      if (currency === '') {
        found.currency = 'Choose the currency this credit is in.';
      } else if (!/^[A-Z]{3}$/.test(currency)) {
        /*
         * Belt and braces — the value can only come from the active master. It
         * refuses rather than folding: `f_currencies` is a case-insensitive
         * collation, so a lower-cased code would match the row it names while
         * having been validated by nothing.
         */
        found.currency = 'Use a three-letter ISO code, e.g. USD.';
      }
    }

    const date = values.creditNoteDate.trim();
    // Shape AND the existence of the day, exactly as the server checks — and
    // compared as a string throughout: `new Date('2026-05-31')` is midnight UTC
    // and renders as 30 May west of Greenwich.
    if (date !== '' && !isRealDate(date)) {
      found.creditNoteDate = 'Must be a real date in YYYY-MM-DD form.';
    }

    return found;
  }

  /* ── save ────────────────────────────────────────────────────────────── */

  const saveDisabled =
    locked ||
    submitting ||
    (isEdit ? !isDirty : values.subject.trim() === '') ||
    /*
     * ⚠️ **Blocked while the source invoice's lines are still arriving**, and
     * this is not politeness about a spinner. Until the read settles there is
     * no way to tell "every line" from "these ones", so a create pressed
     * through that second would have to guess — and the cheap guess (omit the
     * key) credits the whole invoice, which is the outcome this whole control
     * exists to make deliberate.
     */
    (sourceInvoiceId !== null && sourceLines.loading) ||
    lineSelectionEmpty ||
    missingCities;

  /**
   * The one reason Save may be off that the form itself does not explain.
   *
   * An unedited form explains its own greyed-out Save and a required Subject is
   * visibly empty; an untouched-looking list with nothing ticked does not.
   *
   * ⚠️ The two can never both apply — `locked` is edit-only and an empty line
   * selection is create-only — so one slot carries whichever is in force.
   */
  const disabledReason =
    lockedReason ??
    (everyLineCredited
      ? 'Every line on this invoice has already been credited.'
      : lineSelectionEmpty
        ? 'Tick at least one line to credit.'
        : missingCities
          ? 'Choose at least one city on each ticked line.'
          : null);

  /** Runs the client checks and shows what they found. `true` = clean. */
  function checkBeforeSave(): boolean {
    const found = validate();
    setClientErrors(found);
    setServerErrors({});
    setFormError(null);
    return Object.keys(found).length === 0;
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitLock.current || saveDisabled) return;
    if (!checkBeforeSave()) return;
    void save(undefined);
  }

  /**
   * "Save and submit" / "Save and approve": checked first, so the confirmation
   * never opens over a form the save would refuse.
   */
  function requestFollowUp(followUp: CreditNoteFollowUp) {
    if (submitLock.current || saveDisabled) return;
    if (!checkBeforeSave()) return;
    setConfirmFollowUp(followUp);
  }

  async function save(followUp: CreditNoteFollowUp | undefined) {
    if (submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    setSavingAs(followUp ?? 'plain');

    try {
      let saved: CreditNote;
      if (creditNote) {
        const payload = buildUpdatePayload(changedKeys, values);
        saved = (await api.updateCreditNote(creditNote.id, payload)).data;
      } else if (coordinates !== null) {
        saved = (await api.createCreditNote(buildCreatePayload(values, coordinates))).data;
      } else {
        // Unreachable: `coordinates` is non-null on every create. Stated rather
        // than cast, so a later refactor that breaks the invariant fails here
        // instead of silently sending a create with no company.
        return;
      }
      /*
       * ⚠️ **The selection is computed HERE, not held in state**, so what is
       * handed over is derived from the list that is actually on screen at the
       * moment of the save rather than from a snapshot taken when it loaded.
       * It answers `undefined` on every branch that must copy nothing — an
       * edit, a note raised from no invoice, and an invoice with no lines.
       */
      onSaved(saved, lineSelection(), isEdit ? undefined : followUp);
      onClose();
    } catch (caught) {
      if (caught instanceof ApiError) {
        /*
         * ⚠️ **Ordered by SPECIFICITY, never by which shape arrived first.**
         * `routeFieldErrors` splits a 422's details against THIS branch's own
         * write-key list: `fields` may be marked and counted, and everything a
         * control here cannot carry — an unrecognised key, an empty path, a path
         * naming a field this branch does not render — is bannered rather than
         * silently dropped. `unroutable` already carries `formMessages`, so the
         * bare `message` is the last resort and only when nothing else said
         * anything: on a 422 it is only ever "Validation failed".
         */
        const { fields, unroutable } = caught.routeFieldErrors(
          isEdit ? EDIT_WRITE_KEYS : CREATE_WRITE_KEYS,
        );
        setServerErrors(fields);
        if (unroutable.length > 0) setFormError(unroutable.join(' '));
        else if (Object.keys(fields).length === 0) setFormError(caught.message);
      } else {
        setFormError('Could not reach the server. Check your connection and try again.');
      }
      setSubmitting(false);
      setSavingAs(null);
    } finally {
      submitLock.current = false;
    }
  }

  const fieldErrorCount = Object.keys(errors).length;

  /* ── render ──────────────────────────────────────────────────────────── */

  return (
    <Modal
      open
      onClose={onClose}
      /*
       * `lg`, not `xl`. `xl` is for a form whose content is a TABLE — the three
       * document forms and their line grids. This is a column of header fields,
       * and 80rem would spread nine of them across a width that reads as a
       * scattering rather than a form.
       */
      size="lg"
      dismissOnBackdrop={false}
      initialFocusRef={subjectRef}
      title={creditNote ? `Edit ${creditNote.subject}` : 'New credit note'}
      /*
       * 🔴 **THE HEADER, NOT THE FOOTER — the shape the three document forms
       * took on 2026-09-14.** `headerActions` is an ALTERNATIVE to `footer`,
       * never an addition: the same pair in both slots puts two identical Saves
       * on one dialog, which reads as two different saves rather than one
       * action. The pair moves together, and the why-Save-is-off sentence
       * travels WITH Save, because its whole job is to explain the button
       * beside it.
       */
      headerActions={
        <>
          {/*
            The one reason Save may be off that a reader cannot otherwise see.
            An unedited form explains its own greyed-out Save; a status rule does
            not. ⚠️ It cannot go on the Button's `title` — `Button` carries
            `disabled:pointer-events-none`, so a disabled one never receives the
            hover and the sentence would never render.
          */}
          {disabledReason !== null && (
            <p className="self-center text-xs break-words text-danger sm:mr-auto">
              {disabledReason}
            </p>
          )}
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          {canSubmitFollowUp && (
            <Button
              variant="secondary"
              onClick={() => requestFollowUp('submit')}
              loading={savingAs === 'submit'}
              disabled={saveDisabled}
            >
              Save and submit for approval
            </Button>
          )}
          {canApproveFollowUp && (
            <Button
              variant="secondary"
              onClick={() => requestFollowUp('approve')}
              loading={savingAs === 'approve'}
              disabled={saveDisabled}
            >
              Save and approve
            </Button>
          )}
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={savingAs === 'plain' || (isEdit && submitting)}
            disabled={saveDisabled}
          >
            {isEdit ? 'Save changes' : 'Create credit note'}
          </Button>
        </>
      }
    >
      {/*
        The follow-up's confirmation, naming its consequence. ⚠️ Both say
        nothing reaches the customer (owner 2026-10-28) — one shared sentence
        from `@/lib/credit-notes`, never re-worded here.
      */}
      <ConfirmDialog
        open={confirmFollowUp !== null}
        onClose={() => setConfirmFollowUp(null)}
        onConfirm={() => {
          const followUp = confirmFollowUp;
          setConfirmFollowUp(null);
          if (followUp !== null) void save(followUp);
        }}
        tone="primary"
        title={
          confirmFollowUp === 'approve'
            ? 'Create and approve this credit note?'
            : 'Create and submit this credit note for approval?'
        }
        description={
          <p className="m-0">
            {confirmFollowUp === 'approve'
              ? `The credit note is created and approved in SEED EMERGE and in Xero${
                  sourceInvoice ? ', and applied to the invoice it was raised from' : ''
                }. Any remaining credit becomes spendable against this company’s invoices. An approved credit note cannot be un-approved.`
              : 'The credit note is created and goes to the approvers. It can no longer be edited until it is approved or voided.'}{' '}
            {CREDIT_NOTE_NOT_SENT_NOTE}
          </p>
        }
        confirmLabel={confirmFollowUp === 'approve' ? 'Save and approve' : 'Save and submit'}
      />
      <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-5">
        <FormErrorSummary formError={formError} fieldErrorCount={fieldErrorCount} />

        <fieldset className="min-w-0" disabled={locked}>
          <legend className="sr-only">Credit note details</legend>

          {/*
            One column below `sm`, two from `sm` up. `[&>*]:min-w-0` because a
            grid item defaults to `min-width: auto`, so one wide control would
            widen its whole track instead of shrinking inside it.
          */}
          <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2 [&>*]:min-w-0">
            <div
              className="sm:col-span-2"
              ref={(node) => {
                subjectRef.current = node?.querySelector('input') ?? null;
              }}
            >
              <Field label="Subject" required error={errors.subject}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    value={values.subject}
                    invalid={Boolean(errors.subject)}
                    aria-describedby={describedBy}
                    onChange={(event) => setField('subject', event.target.value)}
                  />
                )}
              </Field>
            </div>

            {/*
              🔴 **COMPANY — settable on a CREATE and on no other branch.**
              On an edit there is no control, no `FormValues` key and no payload
              that could name it: `(companyId, currency)` is the identity a
              credit balance is struck at, and moving one after an allocation
              exists moves money between accounts with a `200`.
            */}
            {coordinates === null ? (
              <Field label="Company">
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    value={creditNote?.company.name ?? ''}
                    readOnly
                    aria-readonly="true"
                    className="bg-surface-sunken text-fg-secondary"
                    aria-describedby={describedBy}
                  />
                )}
              </Field>
            ) : lockedCompany ? (
              /*
               * Read-only rather than a disabled Combobox: there is nothing to
               * open, and a disabled picker invites the reader to go looking for
               * the one option it has. The server can still refuse the id, so
               * the error is rendered here as well.
               */
              <Field label="Company" required error={errors.companyId}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    value={lockedCompany.name}
                    readOnly
                    aria-readonly="true"
                    invalid={Boolean(errors.companyId)}
                    className="bg-surface-sunken text-fg-secondary"
                    aria-describedby={describedBy}
                  />
                )}
              </Field>
            ) : (
              <Field
                label="Company"
                required
                error={
                  errors.companyId ?? (companies.failed ? 'Couldn’t load the company list.' : undefined)
                }
              >
                {({ id, 'aria-describedby': describedBy }) => (
                  <Combobox
                    id={id}
                    value={coordinates.companyId}
                    onChange={onCompanyChange}
                    options={companies.options}
                    onSearch={companies.setTerm}
                    loading={companies.loading}
                    searchPlaceholder="Search companies…"
                    footnote={companiesFootnote}
                    invalid={Boolean(errors.companyId)}
                    aria-describedby={describedBy}
                  />
                )}
              </Field>
            )}

            {/* 🔴 CURRENCY — the second coordinate. Same rule, same branch. */}
            {coordinates === null ? (
              <Field label="Currency">
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    value={creditNote?.currency ?? ''}
                    readOnly
                    aria-readonly="true"
                    className="bg-surface-sunken text-fg-secondary"
                    aria-describedby={describedBy}
                  />
                )}
              </Field>
            ) : (
              <Field
                label="Currency"
                required
                error={
                  errors.currency ??
                  (currencies.failed ? 'Couldn’t load the currency list.' : undefined)
                }
              >
                {({ id, 'aria-describedby': describedBy }) => (
                  <Combobox
                    id={id}
                    value={coordinates.currency}
                    onChange={(next) => setCoordinate('currency', next)}
                    options={currencyPickerOptions}
                    loading={currencies.loading}
                    searchPlaceholder="Search currencies…"
                    invalid={Boolean(errors.currency)}
                    aria-describedby={describedBy}
                  />
                )}
              </Field>
            )}

            <Field
              label="Credit reason"
              required
              error={
                errors.creditReasonId ??
                (reasons.failed ? 'Couldn’t load the credit reasons.' : undefined)
              }
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  value={values.creditReasonId}
                  onChange={(next) => setField('creditReasonId', next)}
                  options={reasonOptions}
                  loading={reasons.loading}
                  searchPlaceholder="Search reasons…"
                  invalid={Boolean(errors.creditReasonId)}
                  aria-describedby={describedBy}
                />
              )}
            </Field>

            <Field label="Credit note date" error={errors.creditNoteDate}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  type="date"
                  value={values.creditNoteDate}
                  invalid={Boolean(errors.creditNoteDate)}
                  aria-describedby={describedBy}
                  onChange={(event) => setField('creditNoteDate', event.target.value)}
                />
              )}
            </Field>

            <Field
              label="Contact"
              error={
                errors.contactId ?? (contacts.failed ? 'Couldn’t load the contact list.' : undefined)
              }
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  value={values.contactId}
                  onChange={(next) => setField('contactId', next)}
                  options={contacts.options}
                  onSearch={contacts.setTerm}
                  loading={contacts.loading}
                  emptyOptionLabel="Unassigned"
                  searchPlaceholder="Search contacts…"
                  invalid={Boolean(errors.contactId)}
                  aria-describedby={describedBy}
                />
              )}
            </Field>

            {/*
              ⚠️ **The CUSTOMER's reference**, never ours and never Xero's — so
              nothing here seeds it from a number of ours.
            */}
            <Field label="Reference" error={errors.reference}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  value={values.reference}
                  invalid={Boolean(errors.reference)}
                  aria-describedby={describedBy}
                  onChange={(event) => setField('reference', event.target.value)}
                />
              )}
            </Field>

            <Field
              label="Letterhead"
              error={
                errors.xeroBrandingThemeId ??
                (brandingThemes.error ? brandingThemes.error.message : undefined)
              }
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  value={values.xeroBrandingThemeId}
                  onChange={(next) => setField('xeroBrandingThemeId', next)}
                  options={themeOptions}
                  loading={brandingThemes.loading}
                  // `''` is a real choice: use the organisation's own default.
                  emptyOptionLabel="Organisation default"
                  searchPlaceholder="Search letterheads…"
                  invalid={Boolean(errors.xeroBrandingThemeId)}
                  aria-describedby={describedBy}
                />
              )}
            </Field>

            <div className="sm:col-span-2">
              <Field label="Reason detail" error={errors.creditReasonDetail}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Textarea
                    id={id}
                    rows={3}
                    value={values.creditReasonDetail}
                    invalid={Boolean(errors.creditReasonDetail)}
                    aria-describedby={describedBy}
                    onChange={(event) => setField('creditReasonDetail', event.target.value)}
                  />
                )}
              </Field>
            </div>
          </div>

          {/*
            🔴 **WHICH LINES ARE BEING CREDITED — create-from-an-invoice only.**

            ⚠️ **Outside the two-column grid on purpose.** Every control above is
            one value in a pair of columns; this is a list whose rows carry three
            lines of text each, and half-width it would wrap every one of them.

            ⚠️ **A `<fieldset>` of its own inside the outer one**, so the group
            has a real accessible name rather than a heading that merely sits
            above it — a screen reader meeting these boxes otherwise hears a run
            of product names with nothing saying what ticking one does.
          */}
          {sourceInvoiceId !== null && (
            <fieldset className="mt-5 min-w-0 border-t border-border pt-4">
              <legend className="sr-only">Lines to credit</legend>

              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-medium text-fg-secondary">
                  Lines to credit
                  {lineChoiceOwed && (
                    <span className="ml-2 font-normal text-fg-muted tabular-nums">
                      {selectedLineCount} of {selectableLines.length} selected
                    </span>
                  )}
                </span>
                {lineChoiceOwed && (
                  <div className="flex gap-2">
                    {/* Nothing excluded is everything credited. */}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setUntickedLineIds(new Set())}
                    >
                      Select all
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setUntickedLineIds(new Set(sourceLineList.map((line) => line.id)))
                      }
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </div>

              {/*
                ⚠️ **NOT helper text — it names a state the reader cannot
                otherwise see.** The copy is a SNAPSHOT and the wording it takes
                is frozen, so somebody who reads this list as a live link will
                expect a later correction to the invoice to follow through to the
                credit note. It never will. The same sentence sits on
                `CopyFromInvoiceDialog`, which does the same thing.
              */}
              <p className="mb-3 text-xs text-fg-muted">
                Each line is copied as it stands now, including its wording. Editing the invoice
                afterwards does not change this credit note.
              </p>

              {sourceLines.loading && <Skeleton className="h-24 w-full" />}

              {/*
                ⛔ **A FAILED READ IS ITS OWN BRANCH AND IT SAYS WHAT WILL
                HAPPEN.** This is the one state in which "every line on this
                invoice, unseen" is what somebody is being offered, so it is the
                one state that says so — and Save stays enabled, because an
                unreadable list is not a reason to refuse a credit note that was
                always going to be for the whole invoice anyway.
              */}
              {!sourceLines.loading && sourceLines.failed !== null && (
                <Notice tone="warning">
                  {sourceLines.failed} Its lines could not be listed, so they cannot be picked over —
                  creating this note will credit <strong>every line</strong> on that invoice.
                </Notice>
              )}

              {/*
                ⚠️ **An invoice with NO lines is a different sentence again**, and
                it must not read as a failure. Nothing is copied and the note is
                created empty, which is a legitimate note somebody then adds
                lines to by hand.
              */}
              {!sourceLines.loading && sourceLineList !== null && sourceLineList.length === 0 && (
                <p className="text-xs text-fg-muted">
                  That invoice has no line items, so this credit note starts empty. Add its lines on
                  the credit note itself.
                </p>
              )}

              {lineChoiceOwed && (
                <ul className="grid gap-1">
                  {sourceLineList.map((line) => {
                    const credited = isFullyCredited(line);
                    const ticked = !credited && !untickedLineIds.has(line.id);
                    const lineCities = sourceCitiesOf(line);
                    const chosen = chosenCityIdsOf(line);
                    const label = (
                      <span className="grid gap-0.5">
                        <span className="text-sm text-fg">{line.product.productName}</span>
                        {line.description !== null && (
                          <span className="text-xs text-fg-muted">{line.description}</span>
                        )}
                        {/*
                          ⚠️ The figures are the INVOICE's own, in the
                          invoice's currency — not the note's. They are the
                          same code by the time a copy is allowed (the server
                          refuses a mismatch), but this list is a reading of
                          the source document and is labelled as one.
                        */}
                        <span className="text-xs tabular-nums text-fg-subtle">
                          {line.quantity} × {formatDecimalString(line.unitPrice)} ={' '}
                          {formatDecimalString(line.lineTotal)} {sourceInvoice?.currency ?? ''}
                        </span>
                      </span>
                    );
                    return (
                      <li
                        key={line.id}
                        className="grid gap-2 rounded-md border border-border px-3 py-2"
                      >
                        {/*
                          ⚠️ A line whose every city is already credited is
                          listed, unticked and disabled, with its reason on a
                          WRAPPER — a disabled control receives no hover — and
                          also as a visible line (touch has no hover). A
                          disabled reason, so it is not helper text.
                        */}
                        {credited ? (
                          <div className="grid gap-1">
                            <Tooltip label={ALL_CITIES_CREDITED_REASON} className="block">
                              <span className="block">
                                <Checkbox checked={false} disabled onChange={() => {}} label={label} />
                              </span>
                            </Tooltip>
                            <p className="text-xs text-fg-muted">{ALL_CITIES_CREDITED_REASON}</p>
                          </div>
                        ) : (
                          <Checkbox
                            checked={ticked}
                            onChange={(event) => toggleLine(line.id, event.target.checked)}
                            label={label}
                          />
                        )}
                        {/*
                          The cities, on the same screen as the tick (owner
                          2026-10-27). Outside the Checkbox's <label> so a click
                          in the picker cannot toggle the line.
                        */}
                        {ticked && lineCities.length > 0 && (
                          <div className="grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
                            <Field
                              label="Cities"
                              error={chosen.length === 0 ? 'Choose at least one city.' : undefined}
                            >
                              {({ id }) => (
                                <MultiCombobox
                                  id={id}
                                  values={chosen}
                                  onChange={(next) =>
                                    setCityChoices((prev) => new Map(prev).set(line.id, next))
                                  }
                                  options={availableCitiesOf(line).map((city) => ({
                                    value: String(city.id),
                                    label: city.name,
                                  }))}
                                  summaryNoun={{ singular: 'city', plural: 'cities' }}
                                  selectAll
                                  invalid={chosen.length === 0}
                                  disabled={submitting}
                                />
                              )}
                            </Field>
                            <div className="grid gap-1 sm:justify-items-end">
                              <span className="text-xs font-medium text-fg-secondary">Credit</span>
                              <Money
                                amount={creditLineTotal(line, chosen.length)}
                                currency={sourceInvoice?.currency ?? null}
                                className="text-sm sm:h-9 sm:leading-9"
                              />
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              {/*
                ⛔ **THE REFUSAL SENTENCE IS NOT REPEATED HERE, and it was
                until a browser run showed it twice on one dialog.**

                It travels with Save, in `headerActions` — this repo's rule for
                a why-this-is-disabled sentence, and the right one here for a
                second reason: the modal BODY scrolls while the header does not,
                so on a long invoice the copy under the list is the copy a
                reader may never see. Two identical sentences one scroll apart
                read as a rendering fault rather than as one rule stated twice.

                ⚠️ **What stays local is the COUNT** — `0 of 2 selected`, in the
                section header above — which is the signal at the point of the
                gesture and says something the header sentence does not.

                ⚠️ Neither `tsc` nor oxlint could see the duplication; both were
                clean over it. It is on this list of things only a browser
                finds, beside the four this file already records.
              */}
            </fieldset>
          )}
        </fieldset>
      </form>
    </Modal>
  );
}
